import type { trackEcommerce, trackEvent } from '@svs/nova-core/nextjs/analytics';
import type { Role } from '@svs/nova-core/nextjs/auth';
import type { useDialog } from '@svs/nova-foundation/nextjs/dialog';

import type { CouponIndex } from '../../../../schema/coupon/entry.js';
import type { WalletType } from '../../../../schema/frameworks/entry.js';
import type { Commission } from '../../../../schema/input/entry.js';
import type { ActivatedDrawId } from '../../../../schema/marketplace/entry.js';
import { type ICouponEntity, type ICreateExternalCouponWagerUseCase } from '../../../domain/entry.js';

export type ICreateExternalWagerController = ReturnType<typeof createExternalWagerController>;

type ControllerArgs = {
  wallets: Record<CouponIndex, WalletType | ActivatedDrawId> | undefined;
  coupon: ICouponEntity;
  couponIndex: CouponIndex;
  showDialog: ReturnType<typeof useDialog>['showDialog'];
  paymentContext: {
    role: Role;
    commissionPercent?: Commission;
    activatedDrawId?: ActivatedDrawId;
    paymentUrls?: Record<string, string | undefined>;
    marketplaceWallets?: Record<ActivatedDrawId, { balance: number; soldShares: number; sharePrice: number }>;
    trackTransaction: typeof trackEcommerce;
    trackEventFunction: typeof trackEvent;
  };
};

/**
 * Controller to create external wagers.
 *
 * @param createExternalCouponWagerUseCase Use case for creating wagers from provided coupons
 * @returns Controller executor function
 */
export function createExternalWagerController(createExternalCouponWagerUseCase: ICreateExternalCouponWagerUseCase) {
  return async function controller({
    wallets,
    coupon,
    couponIndex,
    showDialog,
    paymentContext,
  }: ControllerArgs): ReturnType<ICreateExternalCouponWagerUseCase['execute']> {
    const wagerResponse = await createExternalCouponWagerUseCase.execute({
      coupon,
      wallets,
      couponIndex,
      showDialog,
      paymentContext,
    });

    return wagerResponse;
  };
}
